import React from 'react';
import { Box, CircularProgress, List, Stack, Typography } from '@mui/material';

interface ListViewShellProps {
  itemCount: number;
  loading: boolean;
  emptyMessage: string;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyHelperText?: string;
  columnHeaders?: React.ReactNode;
  children: React.ReactNode;
  'data-testid'?: string;
}

export const ListViewShell: React.FC<ListViewShellProps> = ({
  itemCount,
  loading,
  emptyMessage,
  emptyIcon,
  emptyTitle,
  emptyHelperText,
  columnHeaders,
  children,
  'data-testid': testId,
}) => {
  if (loading && itemCount === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (itemCount === 0) {
    if (emptyIcon || emptyTitle || emptyHelperText) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 300,
            p: 4,
          }}
          data-testid="list-empty-state"
        >
          <Stack spacing={2} alignItems="center" sx={{ maxWidth: 400 }}>
            {emptyIcon}
            {emptyTitle && (
              <Typography variant="h6" color="text.primary">
                {emptyTitle}
              </Typography>
            )}
            {emptyHelperText && (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {emptyHelperText}
              </Typography>
            )}
          </Stack>
        </Box>
      );
    }

    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', py: 6 }}
      >
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box data-testid={testId}>
      {columnHeaders && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 2, sm: 3 },
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {columnHeaders}
        </Box>
      )}

      <List disablePadding>{children}</List>
    </Box>
  );
};
